import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Drawer } from "antd";
import {
  AppstoreOutlined,
  HistoryOutlined,
  KeyOutlined,
  LogoutOutlined,
  MenuOutlined,
  QuestionCircleOutlined,
  SettingOutlined,
} from "@ant-design/icons";
import { useNavigate, useLocation } from "react-router-dom";
import { signOut } from "firebase/auth";
import { auth } from "../../firebase";
import { useAuth } from "../../context/useAuth";
import { useAdminAlertCounters } from "../../context/adminAlertCounters";

const ADMIN_ORDER_WARMUP_ENDPOINTS = [
  "https://asia-southeast1-mailcinemaotp.cloudfunctions.net/createManualOrder",
  "https://asia-southeast1-mailcinemaotp.cloudfunctions.net/cancelOrder",
  "https://asia-southeast1-mailcinemaotp.cloudfunctions.net/sendOrderMessage",
  "https://asia-southeast1-mailcinemaotp.cloudfunctions.net/getProductGroupsByApp",
  "https://asia-southeast1-mailcinemaotp.cloudfunctions.net/createDisneyPinAdmin",
  "https://asia-southeast1-mailcinemaotp.cloudfunctions.net/getAdminProductGroups",
  "https://asia-southeast1-mailcinemaotp.cloudfunctions.net/getAdminApps",
  "https://asia-southeast1-mailcinemaotp.cloudfunctions.net/changeWaitingOrderProduct",
];
const ADMIN_ORDER_WARMUP_INTERVAL_MS = 10 * 60 * 1000;
const ADMIN_ORDER_WARMUP_DEDUPE_MS = 2 * 60 * 1000;
let lastAdminOrderWarmupAt = 0;

const warmAdminOrderFunctions = () => {
  if (document.visibilityState === "hidden") return;

  const now = Date.now();
  if (now - lastAdminOrderWarmupAt < ADMIN_ORDER_WARMUP_DEDUPE_MS) return;
  lastAdminOrderWarmupAt = now;

  ADMIN_ORDER_WARMUP_ENDPOINTS.forEach((url) => {
    fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ warmup: true }),
      cache: "no-store",
      keepalive: true,
    }).catch((error) => {
      console.debug("Admin function warm-up skipped:", url, error?.message);
    });
  });
};

export default function AdminLayout({ title, children }) {
  const { user } = useAuth();
  const { waitingOrderCount, cancelRequestCount } = useAdminAlertCounters();

  const navigate = useNavigate();
  const location = useLocation();

  const [openMenu, setOpenMenu] = useState(false);

  useEffect(() => {
    // Keep the existing periodic warm-up behavior; the two order actions also
    // receive an extra just-in-time warm-up from their respective UI flows.
    if (!user || location.pathname !== "/order") return undefined;

    const initialWarmupId = window.setTimeout(() => {
      warmAdminOrderFunctions();
    }, 4000);

    const intervalId = window.setInterval(() => {
      warmAdminOrderFunctions();
    }, ADMIN_ORDER_WARMUP_INTERVAL_MS);

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        warmAdminOrderFunctions();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      window.clearTimeout(initialWarmupId);
      window.clearInterval(intervalId);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [user, location.pathname]);

  useEffect(() => {
    if (!user || location.pathname !== "/admin") return undefined;

    const preloadAdminPages = () => {
      Promise.all([
        import("../../page/orderList"),
        import("../../page/pinDisneyList"),
        import("../../page/appManage"),
        import("../../page/helpManage"),
      ]).catch((error) => {
        console.debug("Admin page preloading skipped:", error?.message);
      });
    };

    if (typeof window.requestIdleCallback === "function") {
      const idleId = window.requestIdleCallback(preloadAdminPages, {
        timeout: 2000,
      });
      return () => window.cancelIdleCallback(idleId);
    }

    const timeoutId = window.setTimeout(preloadAdminPages, 1000);
    return () => window.clearTimeout(timeoutId);
  }, [user, location.pathname]);

  useEffect(() => {
    if (!user || location.pathname !== "/admin") return undefined;

    const preloadAdminPages = () => {
      Promise.all([
        import("../../page/orderList"),
        import("../../page/pinDisneyList"),
        import("../../page/appManage"),
        import("../../page/helpManage"),
      ]).catch((error) => {
        console.debug("Admin page preloading skipped:", error?.message);
      });
    };

    if (typeof window.requestIdleCallback === "function") {
      const idleId = window.requestIdleCallback(preloadAdminPages, {
        timeout: 2000,
      });
      return () => window.cancelIdleCallback(idleId);
    }

    const timeoutId = window.setTimeout(preloadAdminPages, 1000);
    return () => window.clearTimeout(timeoutId);
  }, [user, location.pathname]);

  const displayedActionOrderCount =
    waitingOrderCount + cancelRequestCount;

  const logout = async () => {
    await signOut(auth);

    navigate("/login", {
      replace: true,
    });
  };

  const MENUS = [
   
    {
      label: "สินค้า",
      shortLabel: "สินค้า",
      path: "/admin",
      icon: <AppstoreOutlined />,
    },
    {
      label: "ประวัติทำรายการ",
      shortLabel: "ออเดอร์",
      path: "/order",
      icon: <HistoryOutlined />,
    },
    // {
    //   label: "รหัสยืนยัน",
    //   path: "/pin",
    // },
    {
      label: "รหัสยืนยัน Disney",
      shortLabel: "Disney PIN",
      path: "/pinDisney",
      icon: <KeyOutlined />,
    },
     {
      label: "จัดการ APP",
      shortLabel: "APP",
      path: "/appManage",
      icon: <SettingOutlined />,
    },
    {
      label: "ศูนย์ช่วยเหลือ",
      shortLabel: "ช่วยเหลือ",
      path: "/helpManage",
      icon: <QuestionCircleOutlined />,
    },
  ];

  const isMenuActive = (path) =>
    path === "/admin"
      ? location.pathname === path || location.pathname.startsWith("/admin/")
      : location.pathname === path;

  return (
    <>
      {/* DESKTOP HEADER */}
      <div className="neo-admin-header">
        <div className="neo-admin-logo" onClick={() => navigate("/admin")}>
          <img src="/icon120.jpg" alt="Cinema" />
          <span>ADMIN PANEL</span>
        </div>

        <div className="neo-admin-menus">
          {MENUS.map((menu) => (
            <div
              key={menu.path}
              onClick={() => navigate(menu.path)}
              className={`neo-admin-menu ${
                isMenuActive(menu.path) ? "active" : ""
              }`}
            >
              {menu.label}
              {menu.path === "/order" && displayedActionOrderCount > 0 ? (
                <span className="neo-header-waiting-badge">
                  {displayedActionOrderCount.toLocaleString()}
                </span>
              ) : null}
            </div>
          ))}
        </div>

        <div className="neo-admin-user">
          <span>{user?.email}</span>

          <LogoutOutlined
            style={{
              cursor: "pointer",
            }}
            onClick={logout}
          />
        </div>
      </div>

      {/* MOBILE HEADER */}
      <div className="neo-mobile-header">
        <button
          type="button"
          className="neo-mobile-menu-trigger"
          aria-label="เปิดเมนูผู้ดูแลระบบ"
          onClick={() => setOpenMenu(true)}
        >
          <MenuOutlined />
          {displayedActionOrderCount > 0 ? (
            <span className="neo-mobile-menu-waiting-badge">
              {displayedActionOrderCount > 99
                ? "99+"
                : displayedActionOrderCount.toLocaleString()}
            </span>
          ) : null}
        </button>

        <div
          className="neo-mobile-brand"
          onClick={() => navigate("/admin")}
        >
          <img src="/icon120.jpg" alt="Cinema" />
        </div>
      </div>

      {/* MOBILE DRAWER */}
      <Drawer
        title="Admin Menu"
        placement="left"
        open={openMenu}
        onClose={() => setOpenMenu(false)}
        styles={{
          body: {
            background: "var(--cinema-black)",
            color: "white",
          },
          header: {
            background: "var(--cinema-black)",
            color: "white",
            borderBottom: "1px solid rgba(var(--cinema-white-rgb),0.1)",
          },
        }}
      >
        <div className="neo-drawer-menu-wrap">
          {MENUS.map((menu) => (
            <div
              key={menu.path}
              className={`neo-drawer-menu ${
                isMenuActive(menu.path) ? "active" : ""
              }`}
              onClick={() => {
                navigate(menu.path);
                setOpenMenu(false);
              }}
            >
              {menu.label}
              {menu.path === "/order" && displayedActionOrderCount > 0 ? (
                <span className="neo-header-waiting-badge">
                  {displayedActionOrderCount.toLocaleString()}
                </span>
              ) : null}
            </div>
          ))}

          <div className="neo-drawer-menu logout" onClick={logout}>
            Logout
          </div>
        </div>
      </Drawer>

      {/* CONTENT */}
      {children}

      {/* MOBILE / TABLET BOTTOM NAVIGATION */}
      {createPortal(
        <nav
          className="neo-mobile-bottom-nav"
          aria-label="เมนูผู้ดูแลระบบด้านล่าง"
          onPointerDown={(event) => event.stopPropagation()}
          onPointerUp={(event) => event.stopPropagation()}
          onTouchStart={(event) => event.stopPropagation()}
          onTouchEnd={(event) => event.stopPropagation()}
          onClick={(event) => event.stopPropagation()}
        >
          {MENUS.map((menu) => (
            <button
              key={menu.path}
              type="button"
              className={`neo-mobile-bottom-nav-item ${
                isMenuActive(menu.path) ? "active" : ""
              }`}
              aria-label={menu.label}
              aria-current={isMenuActive(menu.path) ? "page" : undefined}
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                navigate(menu.path);
              }}
            >
              <span className="neo-mobile-bottom-nav-icon">
                {menu.icon}
                {menu.path === "/order" && displayedActionOrderCount > 0 ? (
                  <span className="neo-mobile-bottom-nav-badge">
                    {displayedActionOrderCount > 99
                      ? "99+"
                      : displayedActionOrderCount.toLocaleString()}
                  </span>
                ) : null}
              </span>
              <span>{menu.shortLabel}</span>
            </button>
          ))}
        </nav>,
        document.body,
      )}

      <style>{`
        :root {
          --cinema-red: #e5312a;
          --cinema-red-rgb: 229, 49, 42;
          --cinema-red-dark: #b31d18;
          --cinema-red-dark-rgb: 179, 29, 24;
          --cinema-gold: #f8cc46;
          --cinema-gold-rgb: 248, 204, 70;
          --cinema-gold-light: #ffeb3b;
          --cinema-gold-light-rgb: 255, 235, 59;
          --cinema-white: #ffffff;
          --cinema-white-rgb: 255, 255, 255;
          --cinema-black: #1a1a1a;
          --cinema-black-rgb: 26, 26, 26;
        }

        .neo-admin-header{
          position:absolute;
          top:20px;
          left:20px;
          right:20px;

          z-index:20;

          display:flex;
          justify-content:space-between;
          align-items:center;

          gap:20px;

          padding:14px 22px;

          border-radius:20px;

          background:
            rgba(var(--cinema-black-rgb),.7);

          backdrop-filter:
            blur(24px);

          border:
            1px solid rgba(var(--cinema-white-rgb),.08);
        }

        .neo-admin-logo{
          color:white;
          font-weight:900;
          letter-spacing:2px;
          cursor:pointer;
          white-space:nowrap;

          display:flex;
          align-items:center;
          gap:10px;
        }

        .neo-admin-logo img{
          width:34px;
          height:34px;
          border-radius:10px;
          object-fit:cover;
        }

        .neo-admin-menus{
          flex:1;

          display:flex;
          justify-content:center;
          gap:10px;

          flex-wrap:wrap;
        }

        .neo-admin-menu{
          color:white;

          padding:10px 18px;

          border-radius:12px;

          cursor:pointer;

          transition:.2s;

          font-weight:700;

          display:flex;
          align-items:center;
          gap:7px;
        }

        .neo-header-waiting-badge{
          display:inline-flex;
          min-width:22px;
          height:22px;
          padding:0 6px;
          align-items:center;
          justify-content:center;
          border-radius:999px;
          background:#e5312a;
          color:#fff;
          font-size:11px;
          font-weight:900;
          line-height:1;
          box-shadow:0 4px 12px rgba(229,49,42,.42);
        }

        .neo-admin-menu:hover{
          background:
            rgba(var(--cinema-white-rgb),.08);
          color: var(--cinema-gold);
        }

        .neo-admin-menu.active{
          background:
            linear-gradient(
              135deg,
              var(--cinema-red),
              var(--cinema-gold)
            );
          color: white;

          box-shadow:
            0 10px 25px
            rgba(var(--cinema-red-rgb),.25);
        }

        .neo-admin-user{
          color:white;

          display:flex;
          align-items:center;
          gap:12px;

          font-weight:600;

          white-space:nowrap;
        }

        .neo-mobile-header{
          display:none;
        }

        .neo-mobile-bottom-nav{
          display:none;
        }

        .neo-mobile-menu-trigger{
          position:relative;
          display:inline-flex;
          width:40px;
          height:40px;
          align-items:center;
          justify-content:center;
          border:0;
          border-radius:12px;
          background:rgba(var(--cinema-white-rgb),.08);
          color:var(--cinema-white);
          font-size:20px;
          cursor:pointer;
        }

        .neo-mobile-menu-waiting-badge{
          position:absolute;
          top:-6px;
          right:-8px;
          display:inline-flex;
          min-width:21px;
          height:21px;
          padding:0 5px;
          align-items:center;
          justify-content:center;
          border:2px solid rgba(var(--cinema-black-rgb),.95);
          border-radius:999px;
          background:#e5312a;
          color:#fff;
          font-size:10px;
          font-weight:900;
          line-height:1;
          box-shadow:0 4px 12px rgba(229,49,42,.5);
        }

        .neo-mobile-brand{
          display:flex;
          align-items:center;
          gap:9px;
          cursor:pointer;
          font-weight:800;
        }

        .neo-mobile-brand img{
          width:34px;
          height:34px;
          border-radius:10px;
          object-fit:cover;
        }

        .neo-drawer-menu-wrap{
          display:flex;
          flex-direction:column;
          gap:10px;
        }

        .neo-drawer-menu{
          padding:14px;

          border-radius:12px;

          cursor:pointer;

          background:
            rgba(var(--cinema-white-rgb),.06);

          color:white;

          font-weight:700;

          display:flex;
          align-items:center;
          justify-content:space-between;
          gap:10px;
        }

        .neo-drawer-menu.active{
          background:
            linear-gradient(
              135deg,
              var(--cinema-red),
              var(--cinema-gold)
            );
        }

        .neo-drawer-menu.logout{
          background:
            rgba(239,68,68,.2);
        }

        /* Fold/Duo browsers do not always report pointer: coarse after
           unfolding. Width <= 1180px is compact enough that the desktop
           navigation wraps, so use the mobile header there regardless of
           the pointer value. Wider touch tablets keep the same behavior. */
        @media (max-width: 1180px), (min-width: 1181px) and (max-width: 1366px) and (pointer: coarse){

          .neo-admin-header{
            display:none;
          }

          .neo-mobile-header{
            position:fixed;

            top:env(safe-area-inset-top, 0px);
            left:0;
            right:0;

            z-index:1000;

            height:64px;

            display:flex !important;
            visibility:visible;
            opacity:1;
            align-items:center;
            justify-content:space-between;

            padding:0 18px;

            color:white;

            background:
              rgba(var(--cinema-black-rgb),.85);

            backdrop-filter:
              blur(20px);

            border-bottom:
              1px solid rgba(var(--cinema-white-rgb),.08);
          }

          .neo-mobile-bottom-nav{
            position:fixed;
            right:10px;
            bottom:calc(8px + env(safe-area-inset-bottom));
            left:10px;
            z-index:998;
            display:grid;
            grid-template-columns:repeat(5, minmax(0, 1fr));
            min-height:66px;
            padding:7px;
            border:1px solid rgba(var(--cinema-white-rgb),.1);
            border-radius:20px;
            background:rgba(var(--cinema-black-rgb),.92);
            box-shadow:0 18px 45px rgba(0,0,0,.42);
            backdrop-filter:blur(22px);
            -webkit-backdrop-filter:blur(22px);
            pointer-events:auto !important;
            touch-action:manipulation;
            isolation:isolate;
          }

          .neo-mobile-bottom-nav-item{
            position:relative;
            display:flex;
            min-width:0;
            min-height:52px;
            flex-direction:column;
            align-items:center;
            justify-content:center;
            gap:4px;
            padding:5px 2px;
            border:0;
            border-radius:14px;
            background:transparent;
            color:rgba(var(--cinema-white-rgb),.58);
            font-size:9px;
            font-weight:800;
            line-height:1.1;
            cursor:pointer;
            pointer-events:auto !important;
            touch-action:manipulation;
            -webkit-tap-highlight-color:transparent;
          }

          .neo-mobile-bottom-nav-item > *{
            pointer-events:none;
          }

          .neo-mobile-bottom-nav-item.active{
            background:linear-gradient(
              135deg,
              rgba(var(--cinema-red-rgb),.9),
              rgba(var(--cinema-gold-rgb),.78)
            );
            color:var(--cinema-white);
            box-shadow:0 8px 20px rgba(var(--cinema-red-rgb),.24);
          }

          .neo-mobile-bottom-nav-icon{
            position:relative;
            display:inline-flex;
            align-items:center;
            justify-content:center;
            font-size:19px;
          }

          .neo-mobile-bottom-nav-badge{
            position:absolute;
            top:-8px;
            right:-13px;
            display:inline-flex;
            min-width:18px;
            height:18px;
            padding:0 4px;
            align-items:center;
            justify-content:center;
            border:2px solid rgba(var(--cinema-black-rgb),.95);
            border-radius:999px;
            background:#e5312a;
            color:#fff;
            font-size:8px;
            font-weight:900;
            line-height:1;
          }

          .neo-search-page{
            padding-bottom:calc(104px + env(safe-area-inset-bottom)) !important;
          }

          .neo-search-page > .neo-admin-wrap{
            padding-top:calc(100px + env(safe-area-inset-top, 0px)) !important;
          }

          .neo-search-page,
          .neo-admin-wrap,
          .neo-table,
          .ant-table-wrapper{
            width:100%;
            max-width:100%;
            min-width:0;
            box-sizing:border-box;
          }

          .neo-table .ant-table-container,
          .neo-table .ant-table-content{
            max-width:100%;
            overflow-x:auto !important;
            overscroll-behavior-x:contain;
            -webkit-overflow-scrolling:touch;
          }

          /* Safari uses the dynamic viewport for short pages, so the page can
             end above its floating bottom controls. Keep a large-viewport
             canvas behind the controls even when a list is empty. */
          @supports (height: 100lvh){
            .neo-search-page{
              min-height:100lvh;
            }
          }

          .neo-order-page{
            overflow:visible;
            min-height:100vh;
            min-height:100dvh;
          }

          @supports (height: 100lvh){
            .neo-order-page{
              min-height:100lvh;
            }
          }

        }

        @media (display-mode: standalone) and (max-width: 1180px){
          .neo-mobile-header{
            top:env(safe-area-inset-top, 0px);
            display:flex !important;
            visibility:visible;
            opacity:1;
          }

          .neo-search-page{
            padding-top:env(safe-area-inset-top, 0px);
          }
        }

        /* On an unfolded Fold/Duo in landscape, Safari can anchor bottom
           to the shorter visual viewport segment. Keep the navigation fixed,
           but dock it against the large viewport so it stays at the physical
           bottom edge instead of floating across the middle of the content. */
        @media (max-width: 1180px) and (orientation: landscape){
          @supports (height: 100lvh){
            .neo-mobile-bottom-nav{
              top:calc(100lvh - 74px - env(safe-area-inset-bottom, 0px));
              bottom:auto;
            }
          }
        }

      `}</style>
    </>
  );
}
